// mytna.logic.js
import { summaryData } from './settings';
import { fetchDataByTab, fetchDetailExperientialLearning, fetchDetailSocialLearning, fetchDetailStructuredLearning, fetchDetailStructuredLearningByCompetency, fetchTNAPeriod, fetchSubmitTNA, fetchSummaryData, tabToLearningType } from './api';
import { formatDateShort } from '@/utils/dateFormatter';
import { BIB_APPROVAL_STEP_OPTIONS, BIB_TNA_STATUS_OPTIONS } from '@/utils/approvalStatus';
import { COURSE_TYPE_OPTIONS } from '@/constants/mitraFilters';

export default {
    data() {
        return {
            optionsSelectPeriod: [],
            selectedPeriod: null,
            activeTab: "structured",
            filterModalId: 'modal-filter-my-tna',
            filters: {
                status: null,
                approval_step: null,
                course_type: null,
            },
            statusOptions: BIB_TNA_STATUS_OPTIONS,
            approvalStepOptions: BIB_APPROVAL_STEP_OPTIONS,
            courseTypeOptions: COURSE_TYPE_OPTIONS,
            summaryData: summaryData,
            structuredLearningData: {
                mandatory_trainings: [],
                gap_competencies: [],
            },
            socialLearningData: [],
            experientialLearningData: [],
            isLoadingSummary: false,
            isLoadingTNA: false,
            isLoadingModal: false,
            isSubmitting: false,
            selectedItems: {
                structured: [],
                social: [],
                experiential: [],
            },
            dataModal: null,
            dataModalCompetency: [],
            showDetailModal: false,
            showDetailModalCompetency: false,
            showConfirmationSubmit: false,
            payload: {},
            showAddSocialModal: false,
            showAddExperientialModal: false,
        };
    },

    computed: {
        tabName() {
            return this.activeTab.charAt(0).toUpperCase() + this.activeTab.slice(1);
        },
        activeFiltersCount() {
            const filterKeys = ['status', 'approval_step', 'course_type'];
            return filterKeys.filter((key) => Boolean(this.filters?.[key])).length;
        },
        statusFilterLabel() {
            const option = (this.statusOptions || []).find((item) => item.value === this.filters.status);
            return option?.label || this.filters.status;
        },
        approvalStepLabel() {
            const option = (this.approvalStepOptions || []).find((item) => item.value === this.filters.approval_step);
            return option?.label || this.filters.approval_step;
        },
        courseTypeLabel() {
            const option = (this.courseTypeOptions || []).find((item) => item.value === this.filters.course_type);
            return option?.label || this.filters.course_type;
        },
    },

    watch: {
        selectedPeriod: {
            handler(newVal, oldVal) {
                if (newVal) {
                    // Use this.activeTab instead of tab
                    this.activeTab = this.activeTab;
                    this.showDetailModal = false;
                    this.showDetailModalCompetency = false;
                    this.showAddSocialModal = false;
                    this.showAddExperientialModal = false;
                    this.dataModal = null;
                    this.dataModalCompetency = [];
                    this.payload = {
                        ...this.payload,
                        tna_id: newVal,
                        learning_type: tabToLearningType[this.activeTab],
                    };
                    const payload = this.buildRequestPayload();
                    this.fetchDataByTab(payload);
                    this.fetchSummaryData(payload);
                }
            },
            immediate: false
        }
    },

    methods: {
        handleTabChange(tab) {
            this.activeTab = tab;
            this.showDetailModal = false;
            this.showDetailModalCompetency = false;
            this.showAddSocialModal = false;
            this.showAddExperientialModal = false;
            this.dataModal = null;
            this.dataModalCompetency = [];
            this.payload = {
                ...this.payload,
                learning_type: tabToLearningType[this.activeTab],
            }
            const payload = this.buildRequestPayload();
            this.fetchDataByTab(payload);
            this.fetchSummaryData(payload);
        },
        buildRequestPayload() {
            return {
                ...this.payload,
                status: this.filters.status || undefined,
                approval_step: this.filters.approval_step || undefined,
                course_type: this.filters.course_type || undefined,
            };
        },
        showFilterModal() {
            this.$bvModal.show(this.filterModalId);
        },
        applyFilters(payload = {}) {
            this.filters = {
                ...this.filters,
                status: payload.status || null,
                approval_step: payload.approval_step || null,
                course_type: payload.course_type || null,
            };
            this.reloadFilteredData();
        },
        resetFilters() {
            this.filters = {
                ...this.filters,
                status: null,
                approval_step: null,
                course_type: null,
            };
            this.reloadFilteredData();
        },
        clearSingleFilter(filterKey) {
            if (this.filters[filterKey] === undefined) return;
            this.filters[filterKey] = null;
            this.reloadFilteredData();
        },
        reloadFilteredData() {
            if (!this.payload.tna_id && !this.selectedPeriod) return;
            const payload = this.buildRequestPayload();
            this.fetchDataByTab(payload);
            this.fetchSummaryData(payload);
        },
        async fetchSummaryData(payload) {
            this.isLoadingSummary = true;
            const data = await fetchSummaryData(payload);
            this.summaryData = {
                previous: {
                    plan: 0,
                    approved: 0,
                    completed: 0,
                    percentage: 0,
                    ...(data?.previous || {}),
                },
                current: {
                    plan: 0,
                    approved: 0,
                    completed: 0,
                    percentage: 0,
                    ...(data?.current || {}),
                },
            };
            this.isLoadingSummary = false;
        },
        async fetchDataByTab(payload) {
            this.isLoadingTNA = true;
            const tab = this.activeTab;
            const data = await fetchDataByTab(payload);
            if (tab === 'structured') {
                const mandatoryTrainings = Array.isArray(data?.mandatory_trainings) ? data.mandatory_trainings : [];
                const gapCompetencies = Array.isArray(data?.gap_competencies) ? data.gap_competencies : [];

                this.structuredLearningData.mandatory_trainings = mandatoryTrainings.map(item => ({
                    ...item,
                    mandatory: true,
                    remarks: item.remarks || 'mandatory',
                }));
                this.selectedItems.structured = this.structuredLearningData.mandatory_trainings;
                this.structuredLearningData.gap_competencies = gapCompetencies.map(item => ({
                    ...item,
                    mandatory: false,
                    title: item.competency,
                    competencies: {
                        role_competency: item.role_competency_id,
                        type: item.role_competency_type
                    },
                    cover_image_path: item.thumbnail_url,
                }));
            } else if (tab === 'social') {
                this.socialLearningData = data.data.map(item => ({
                    ...item,
                    cover_image_path: item?.coaching?.cover_image,
                    title: item?.coaching?.title,
                    description: item?.coaching?.methodology,
                    competencies: {
                        role_competency: item?.role_competency_id,
                        type: item?.role_competency_type
                    },
                })) || [];
            } else {
                this.experientialLearningData = data.data.map(item => ({
                    ...item,
                    iel_id: item.iel_id || item.id,
                    cover_image_path: null,
                    title: item?.project?.name,
                    description: item?.project?.related_competencies?.map(comp => comp.competency_name).join(', '),
                })) || [];
            }

            this.isLoadingTNA = false;
        },
        async handleOpenModalDetail(course, type) {
            switch (type) {
                case 'mandatory_trainings':
                    this.isLoadingModal = true;
                    this.showDetailModal = true;
                    this.showDetailModalCompetency = false;
                    this.dataModalCompetency = [];
                    this.dataModal = null;
                    this.dataModal = await fetchDetailStructuredLearning(course.id);
                    this.isLoadingModal = false;
                    return;
                case 'gap_competencies':
                    this.isLoadingModal = true;
                    this.showDetailModal = false;
                    this.showDetailModalCompetency = true;
                    this.dataModal = null;
                    this.dataModalCompetency = [];
                    this.dataModalCompetency = await fetchDetailStructuredLearningByCompetency(
                        course.competency_id,
                        this.selectedPeriod
                    );
                    this.isLoadingModal = false;
                    return;
                case 'social':
                    this.isLoadingModal = true;
                    this.showDetailModal = true;
                    this.dataModal = null;
                    const res = await fetchDetailSocialLearning(course.id);
                    this.dataModal = {
                        ...res,
                        title: res?.coaching?.title,
                        description: res?.coaching?.methodology,
                        cover_image_path: res?.coaching?.cover_image,
                    };
                    this.isLoadingModal = false;
                    return;
                case 'experiential':
                    this.isLoadingModal = true;
                    this.showDetailModal = true;
                    this.dataModal = null;
                    const result = await fetchDetailExperientialLearning(course.id);
                    this.dataModal = {
                        ...result,
                        id: result.id,
                        iel_id: result.iel_id || result.project?.iel_id,
                        projectName: result.project.name,
                        description: result.project.description,
                        competencies: result.project.related_competencies,
                        teams: result.project.members,
                        milestones: result.project.activities,
                    };
                    this.isLoadingModal = false;
                    return;
                case 'structured':
                    this.showDetailModal = true;
                    this.dataModal = await fetchDetailStructuredLearning(course.id);
                    this.isLoadingModal = false;
                    return;
                default:
                    this.isLoadingModal = false;
                    return;
            }
        },
        handleSelectChange(course, checked) {
            const list = this.selectedItems[this.activeTab] || [];
            if (checked) {
                if (!list.some(item => item.id === course.id)) {
                    this.selectedItems[this.activeTab] = [...list, course];
                }
            } else {
                this.selectedItems[this.activeTab] = list.filter(item => item.id !== course.id);
            }
        },
        async handleSubmit(activeTab, items, trainingPlan) {
            try {
                this.isSubmitting = true;
                this.$swal({
                    title: 'Mengirim...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });
                // Filter out mandatory trainings and approved items from items
                let filteredItems = items;
                // if (activeTab === 'structured') {
                //     const mandatoryIds = this.structuredLearningData.mandatory_trainings.map(t => t.id);
                //     filteredItems = items.filter(item => {
                //         // Filter out mandatory trainings
                //         if (mandatoryIds.includes(item.id)) return false;
                //         // Filter out items that are already approved
                //         if (item.status && item.status.toLowerCase() === 'approved') return false;
                //         return true;
                //     });
                // } else {
                //     // For social and experiential, also filter out approved items
                //     filteredItems = items.filter(item => {
                //         if (item.status && item.status.toLowerCase() === 'approved') return false;
                //         return true;
                //     });
                // }

                // Build mapped training plan payload: [{ id, training_mapping_id, year }]
                const allowedItemIds = new Set((filteredItems || []).map(item => item.id));
                const mappedTrainingPlan = (trainingPlan || [])
                    .flatMap((column) => {
                        const year = Number(column?.year) || null;
                        const trainings = column?.trainings || [];

                        return trainings
                            .filter((training) => allowedItemIds.has(training.id))
                            .map((training) => ({
                                id: training.id,
                                training_mapping_id: training.training_mapping_id || null,
                                year,
                                mandatory: Boolean(training.mandatory || training.is_mandatory),
                                remarks: training.remarks || (training.mandatory || training.is_mandatory ? 'mandatory' : null),
                            }));
                    });

                const args = {
                    activeTab,
                    items: filteredItems,
                    trainingPlan: mappedTrainingPlan,
                };
                const res = await fetchSubmitTNA(args);
                await this.fetchDataByTab(this.buildRequestPayload());
                if (res.httpCode == 200 || res.httpCode == 201) {
                    this.showConfirmationSubmit = false;
                    this.$swal('Success', 'Submit berhasil!', 'success');
                } else {
                    throw new Error("Submit gagal.");
                }
            } catch (err) {
                this.$swal('Error', `Submit gagal! - ${err?.response?.data?.error}`, 'error');
            } finally {
                this.isSubmitting = false;
                this.showDetailModal = false;
                this.dataModal = null;
            }
        },
        async fetchTNAPeriod() {
            try {
                const res = await fetchTNAPeriod();
                this.optionsSelectPeriod = res.data?.map(item => ({
                    text: `${formatDateShort(item.start_date)} - ${formatDateShort(item.end_date)}`,
                    value: item.id
                })) || [];

                // Auto-select period: pilih yang tanggal sekarang berada di antara start_date dan end_date
                if (this.optionsSelectPeriod.length > 0) {
                    await this.$nextTick();
                    const now = new Date();
                    const activePeriod = (res.data || []).find(item => new Date(item.start_date) <= now && new Date(item.end_date) >= now);
                    this.selectedPeriod = activePeriod?.id || null;
                }
            } catch (error) {
                console.error('Error fetching IDP periods:', error);
            }
        },
    },
    mounted() {
        const payload = {
            learning_type: tabToLearningType[this.activeTab] || 'structured_learning',
        }
        this.payload = payload;
        this.fetchTNAPeriod();
    },
};
